/**
 * The `open_pull_request` tool every /code session offers its agent, through
 * the cubic plugin's session tools (`OPENCODE_PLUGIN_ENV.SESSION_TOOLS_FILE`).
 * The plugin answers every call with the fixed result below; when the turn
 * ends, cubic opens the pull request the call's input describes, as the person
 * who asked (`openRequestedPullRequest` in `pull-request.ts`). The input schema
 * mirrors `openPullRequestInputSchema`; a test keeps the two in step. No Node
 * imports: the session permissions read the name.
 */

import { z } from 'zod';

import type { SessionToolDefinition } from '.';

/** The OpenCode tool the agent calls to have cubic open the session's pull request. */
export const OPEN_PULL_REQUEST_TOOL = 'open_pull_request';

export const openPullRequestInputSchema = z.object({
  title: z.string().trim().min(1),
  body: z.string(),
  ready: z.boolean(),
});

export type OpenPullRequestInput = z.infer<typeof openPullRequestInputSchema>;

/** The tool as the plugin registers it: `input` is its JSON Schema, `result` what every call answers. */
export const OPEN_PULL_REQUEST_TOOL_DEFINITION = {
  name: OPEN_PULL_REQUEST_TOOL,
  description: [
    "Open a pull request from this session's branch, with the person you are working with as its author, so it reaches them and their reviewers as their own work. Use it instead of `gh pr create`, whose pull requests cubic's app authors.",
    'Commit and push the branch first. cubic opens the pull request when your turn ends, from the branch as pushed then, so push any later commits before you finish. If the branch already has an open pull request, your pushes update it; change its title or description with `gh pr edit`.',
  ].join('\n\n'),
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['title', 'body', 'ready'],
    properties: {
      title: {
        type: 'string',
        minLength: 1,
        description: 'A short title that says what the pull request changes.',
      },
      body: {
        type: 'string',
        description: 'The description in Markdown: what changed and why, and the checks you ran.',
      },
      // Ask whether the pull request is ready for review without favoring either state.
      ready: {
        type: 'boolean',
        description:
          'true opens it ready for review; false opens it as a draft, or ready for review in a repository that does not allow drafts.',
      },
    },
  },
  needs: 'sessionRecord',
  // What the model reads before its reply: it has no link yet, since the pull
  // request opens after the turn.
  result:
    'cubic opens the pull request when your turn ends, and its link then shows in the session, and in the Slack thread when the session has one. Tell the user it is being opened; do not look it up or wait for it.',
} as const satisfies SessionToolDefinition;
